<template>
  <div class="dropdown-product-images form-row mb-1">
    <div
      v-if="isFileImage && !isFieldsTab"
      class="col-3 d-flex align-items-center"
    >
      Product
    </div>
    <div
      v-show="isFileImage && !isFieldsTab"
      class="col-9"
    >
      <div class="input-group">
        <input
          v-model="productIds"
          type="text"
          class="form-control product-ids-input"
          :disabled="disabled"
          readonly
          @focus="onFocusProductIds"
        >
        <div class="input-group-append">
          <button
            class="btn btn-outline px-2"
            :disabled="disabled"
            @click="onFocusProductIds(!isShowDropdown)"
          >
            <i :class="{'cil-chevron-bottom': !isShowDropdown, 'cil-chevron-top': isShowDropdown }" />
          </button>
          <button
            v-b-tooltip.hover.topleft
            title="Replace Product Images"
            class="btn btn-outline px-2"
            :disabled="disabled"
            @click="openReplaceMode(!isShowDropdown)"
          >
            <i class="bi bi-repeat" />
          </button>
          <button
            v-b-tooltip.hover.topleft
            title="Clear and Add New Product Images"
            class="btn btn-outline px-2"
            :disabled="disabled"
            @click="openReplaceAllMode(!isShowDropdown, true)"
          >
            <i class="bi bi-arrow-left-right" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-show="isShowDropdown"
      ref="productsContainer"
      class="products-container"
      :class="isContracted ? 'products-container--contracted' : ''"
      :style="{ maxHeight: dropdownMaxHeight }"
    >
      <div class="contract-btn-wrapper">
        <div 
          class="contract-btn" 
          @click="isContracted = !isContracted"
        >
          <b-icon 
            v-if="!isContracted" 
            icon="arrows-angle-contract"
          />
          <b-icon 
            v-else 
            icon="arrows-angle-expand"
          />
        </div>
        <div class="close-btn">
          <b-icon
            icon="x-lg"
            @click="handleClosePopup"
          />
        </div>
      </div>
      <div class="file-browser-wrapper">
        <FileBrowser />
      </div>
      <div class="buttons-wrapper">
        <span
          v-if="isFileImage"
          class="mr-auto"
        >
          {{ selectedCount }} Selected
        </span>
        <b-button
          @click="isShowDropdown = false"
        >
          Close
        </b-button>
        <b-button
          v-if="showReplaceAll"
          variant="primary"
          @click="handleReplaceAll"
        >
          Replace
        </b-button>

        <b-button
          v-if="showReplaceAll"
          variant="primary"
          @click="handleReplaceAllApplyTo"
        >
          Replace in All Templates
        </b-button>

        <b-button
          v-if="!fieldsReplaceAll"
          variant="success"
          @click="handleImagesSave"
        >
          {{ isFileImage ? saveImageText : 'Select' }}
        </b-button>
        <b-button
          v-if="pickedItems?.length && pickedItems?.length > 1 && mode === MODES.REPLACE"
          variant="success"
          @click="handleReplaceAllProductImages"
        >
          Replace All
        </b-button>
        <b-dropdown 
          v-if="pickedItems?.length && mode === MODES.REPLACE"
          split
          dropup
          text="Replace Multiple"
          variant="success"
          class="replace-options-dropdown"
          @click="handleImageReplacement"
        >
          <b-list-group>
            <b-list-group-item>
              <b-form-checkbox 
                v-model="replaceAllProducts"
                @click.stop
              >
                Replace All Product Images
              </b-form-checkbox>
            </b-list-group-item>
            <b-list-group-item>
              <b-form-checkbox
                v-model="replaceOnlyVisible"
                @click.stop
              >
                Replace Visible Product Images
              </b-form-checkbox>
            </b-list-group-item>
            <b-list-group-item>
              <b-form-checkbox
                v-model="applyToAllPreviews"
                @click.stop
              >
                Apply to All Templates
              </b-form-checkbox>
            </b-list-group-item>
          </b-list-group>
        </b-dropdown>
      </div>
    </div>
    <div 
      v-show="isFileImage && !isFieldsTab"
      class="col-12 my-2"
    >
      <ProductImageUploads @replaceImages="replaceProductIds" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ProductImageUploads from "./ProductImageUploads.vue";
import FileBrowser from "@frontend/components/modules/file-browser-product-selector/FileBrowser.vue";
import '@/../sass/includes/product-images-dropdown.scss'
import {mapGetters, mapActions} from "vuex";
import {MODES} from "@frontend/components/modules/file-browser-product-selector/constants";
import { editorControlEventBroker, EVENTS } from "@frontend/group/modules/editor-control-popup/event-broker";
import { TAB_INDEX } from '@frontend/constants/layout-sidebar-tabs';

export default {
  components: {ProductImageUploads, FileBrowser},
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 1,
    },
    isFileImage: {
      type: Boolean,
      default: false,
    },
    isTabActive: {
      type: Boolean,
    },
    isFieldsTab: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      productIds: '',
      showDropdownOnValueUpdate: false,
      products: [],
      selectedProducts: [],
      currentPage: 1,
      totalProducts: 0,
      perPage: 10,
      isShowDropdown: false,
      dropdownMaxHeight: null,
      editorControlEventBroker,
      isContracted: false,
      replaceAllProducts: true,
      replaceOnlyVisible: false,
      applyToAllPreviews: false,
      backToLayersTab: false,
      fieldsReplaceAll: false
    }
  },
  computed: {
    MODES() {
      return MODES
    },
    showReplaceAll () {
      return this.mode === MODES.BROWSER && this.selectedCount && this.isFileImage 
        && !this.isAddingItemsContext;
    },
    ...mapGetters('fileBrowserProductSelector', [
      'pickedItems',
      'mode',
      'selectedCount',
      'modal',
      'selectedItems',
      'isAddingItemsContext'
    ]),
    ...mapGetters('layout', [
      'target', 
      'selectedInstance'
    ]),
    saveImageText() {
      return this.mode === MODES.REPLACE ? 'Replace Selected' : 'Save';
    }
  },
  watch: {
    value() {
      this.productIds = this.value;
      this.isContracted = false;
    },
    isShowDropdown(newValue) {
      this.setOnlyOneItemSelection(newValue)
      this.updateDropdownMaxHeight();
      editorControlEventBroker.fire(EVENTS.PRODUCT_IMAGE_DROPDOWN_VISIBILITY_CHANGED, {
        sourceId: this._uid,
        isOpen: newValue,
      })
    },
    isTabActive() {
      if (!this.isTabActive) {
        this.isContracted = false;
      }
    },
    pickedItems(items) {
      if (this.mode === MODES.BROWSER) {
        // this.setSelection(items.slice(1));
        this.setSelection(items);
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickDropdown.bind(this), true);
    editorControlEventBroker.on(EVENTS.OPEN_REPLACE_PRODUCT_IMAGE, () => {
      this.isShowDropdown = true;
      this.openReplaceMode();
      this.backToLayersTab = true
    });

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.updateDropdownMaxHeight);
  },
  destroyed() {
    document.removeEventListener('click', this.onClickDropdown);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.updateDropdownMaxHeight);
    editorControlEventBroker.fire(EVENTS.PRODUCT_IMAGE_DROPDOWN_VISIBILITY_CHANGED, {
      sourceId: this._uid,
      isOpen: false,
    });
  },
  methods: {
    updateDropdownMaxHeight() {
      if (!this.isShowDropdown) return;

      this.$nextTick(() => {
        const popup = this.$refs.productsContainer;
        if (!popup || !popup.offsetWidth) return;

        const rect = popup.getBoundingClientRect();
        // Rect coordinates include the page's CSS zoom; CSS heights do not.
        const scale = rect.width / popup.offsetWidth;
        const availableHeight = Math.max(0, window.innerHeight - rect.top - 16);
        this.dropdownMaxHeight = `${availableHeight / scale}px`;
      });
    },
    ...mapActions('fileBrowserProductSelector', [
      'setCurrentPickedItems',
      'setOnlyOneItemSelection',
      'disableMoveProductsMode',
      'setSelection',
      'enableReplaceMode',
      'setSelectedCanvasElement',
      'closeModal',
      'setAddItemsContext'
    ]),
    async replaceProductIds(newProductName) {
      const {data} = await axios.get('/file/file_list', {
        params: {
          searchKey: newProductName,
          pageSize: 10,
          pageNumber: this.currentPage,
        }
      });
      this.selectedProducts = [];
      this.onSelectedProduct(data.items.map(product => {
        let productId = product.name.split('.').reverse();
        productId.shift();
        productId = productId.reverse().join('.');
        return {
          ...product,
          productId,
        }
      })[0])
    },
    onClickDropdown(event) {//capture phase
      if (!event.target.closest('.dropdown-product-images') && !this.modal.type && !document.querySelector('.viewer-canvas')) {
        this.isShowDropdown = false;
      }
    },
    onSelectedProduct(product) {
      const selectedProduct = this.selectedProducts.find(_product => product.id ===_product.id);

      if (selectedProduct) {
        this.selectedProducts = this.selectedProducts.filter(_product => product.id !==_product.id)
      } else {
        this.selectedProducts.push(product);
      }

      this.selectedProducts = this.selectedProducts
        .reverse()
        .filter((selectedProduct, index) => index <= (this.limit - 1))
        .reverse();

      this.productIds = this.selectedProducts.map(selectedProduct => {
        return selectedProduct.productId;
      }).join(' ');

      this.$emit('uploadInput', this.productIds);
    },
    onFocusProductIds(isOpen = true, isFieldsTab = false) {
      this.fieldsReplaceAll = isFieldsTab;
      this.disableMoveProductsMode();
      this.selectedInstance.canvas.fire('product-images-selector:init-view', this.selectedInstance.id)
      this.isShowDropdown = isOpen;
    },
    openReplaceAllMode(isOpen = true, isFieldsTab = false) {
      this.fieldsReplaceAll = isFieldsTab;
      this.disableMoveProductsMode();
      this.setAddItemsContext(false);
      this.selectedInstance.canvas.fire('product-images-selector:init-view-browser', this.selectedInstance.id);
      this.isShowDropdown = isOpen;
    },
    openReplaceMode(isOpen = true, isFieldsTab = false) {
      this.fieldsReplaceAll = isFieldsTab;
      this.disableMoveProductsMode();
      const canvas = this.selectedInstance?.canvas;
      const target = canvas.getObjects()
        .find((obj) => obj.id === this.target?.id)
      if (!target) {
        return;
      }
      const simulatedEvent = {target: canvas};
      if (target.canvas) {
        target.canvas = canvas;
      }
      this.setSelectedCanvasElement(target.id)
      this.selectedInstance.canvas.fire('selection:created', { e: simulatedEvent, target });
      canvas.setActiveObject(target);
      canvas.renderAll();
      this.isShowDropdown = isOpen;
      this.enableReplaceMode()
    },
    openActiveReplaceMode(isOpen = true, isFieldsTab = false) {
      this.fieldsReplaceAll = isFieldsTab;
      //object already active
      this.disableMoveProductsMode();
      const canvas = this.selectedInstance?.canvas;
      let target = canvas.getObjects()
        .find((obj) => obj.id === this.target?.id)
      if (!target) {
        // check if the canvas active object is the same object
        const activeObject = canvas.getActiveObject();
        if (activeObject?.id !== this.target.id) {
          return;
        }

        target = activeObject;
      }

      if (target.canvas) {
        target.canvas = canvas;
      }
      this.setSelectedCanvasElement(target.id)
      this.isShowDropdown = isOpen;
      this.enableReplaceMode()
      this.setAddItemsContext(false);
    },
    handleImagesSave() {
      if (!this.isFileImage) {
        let selectedPath = this.selectedItems[this.selectedItems.length - 1]?.full_url;

        if (!selectedPath) {
          selectedPath = this.pickedItems[this.pickedItems.length - 1]?.full_url;
        }
        
        if (!selectedPath) {
          console.error('No product image selected');
          return;
        }
        
        if (!selectedPath.includes('http') && !selectedPath.includes('https') && !selectedPath.startsWith('/')) {
          selectedPath = `/${selectedPath}`;
        }

        const base_url = window.location.origin;
        let path = selectedPath.includes('http') || selectedPath.includes('https') ? 
          selectedPath : `${base_url}${selectedPath}`;
        this.replaceTargetImage(path);
        this.isShowDropdown = false;
        return;
      }

      switch (this.mode) {
        case MODES.REPLACE:
          this.selectedInstance.canvas.fire('product-images-selector:replace-product-image', this.selectedInstance.id);
          break;
        case MODES.BROWSER:
          this.selectedInstance.canvas.fire('product-images-selector:save-images', this.selectedInstance.id);
          break;
        default:
          this.isShowDropdown = false
      }
    },
    handleReplaceAll () {
      this.selectedInstance.canvas.fire('product-images-selector:replace-all', this.selectedInstance.id);
    },
    handleReplaceAllApplyTo () {
      this.selectedInstance.canvas.fire('product-images-selector:replace-all-and-apply-to', this.selectedInstance.id);
    },
    replaceTargetImage(path) {
      if (this.$store.getters['layout/target']) {
        const target = this.$store.getters['layout/target'];
        const instanceId = this.$store.getters['layout/selectedInstance'].id;
        
        this.editorControlEventBroker.fire('IMAGE_CHANGED', {
          action: 'removeBackgroundThemeImgBG',
          value: {
            imgUrl: path,
            objectId: target.id,
            instanceId: instanceId,
            target: target,
            isFromDropdown: true
          }
        });
      }
      
      this.isShowDropdown = false;
    },
    handleClosePopup() {
      this.isShowDropdown = false;
      this.isContracted = false;
      if (this.backToLayersTab) {
        const  layoutSidebar = document.querySelector('layout-sidebar-ce').getVueInstance()
        layoutSidebar.tabIndex = TAB_INDEX.LAYERS;
        this.backToLayersTab = false;
      }
    },
    async handleReplaceAllProductImages() {
      if (!this.pickedItems?.length) return;
      this.selectedInstance.canvas.fire('product-images-selector:replace-and-apply-to', {instanceId: this.selectedInstance.id, replacedObj: this.pickedItems[0]});
    },
    async handleImageReplacement() {
      if (!this.pickedItems?.length) return;
      this.selectedInstance.canvas.fire('product-images-selector:replace-all-similar-images', 
        {
          instanceId: this.selectedInstance.id, replacedObj: this.pickedItems[0], options: {
          replaceAllProducts: this.replaceAllProducts,
          replaceOnlyVisible: this.replaceOnlyVisible, 
          applyToAllPreviews: this.applyToAllPreviews,
          closeModal: () => {
              this.handleClosePopup();
          }
      }});
      
    },
    handleKeyDown(e) {
      if (e.key === 'Escape') {
        this.handleClosePopup();
      }
    }

  }
}
</script>
<style scoped lang="scss">
.dropdown-product-images {
  & input:hover { 
    cursor: pointer;
  }
}

.product-ids-input[readonly] {
    background: #fff;
}

.file-browser-wrapper {
    min-height: 0;
    max-height: 770px;
    overflow-y: auto;
    ::v-deep {
        .c-main-view {
            border: none;
        }
    }
}

.buttons-wrapper {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  margin: 15px 10px 0 0;
  justify-content: end;
}
::v-deep {
  .products-container {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: calc(65vw - 9px);
    height: fit-content;
    max-height: calc(100vh - 103px);
    position: fixed;
    background: rgb(255, 255, 255);
    z-index: 1000;
    top: 87px;
    right: 64px;
    border: 1px solid;
    box-shadow: 2px 2px #888888;
    overflow: visible;
    padding: 20px;
  }
  
  .contract-btn-wrapper {
    display: flex;
    flex-shrink: 0;
    justify-content: space-between;
  }

  .contract-btn, .close-btn {
    cursor: pointer;
  }

  .products-container--contracted {
    width: 400px;
    padding: 5px 20px;
    
    .file-browser-wrapper {
      display: none;
    }
    
    .buttons-wrapper {
      display: none;
    }
  }
}

.replace-options-dropdown {
  .list-group {
    min-width: 300px;
    .list-group-item {
      &:first-child {
        border-top: 0;
      }
      &:last-child {
        border-bottom: 0;
      }
    }
    
    
  }
  .dropdown-menu.show {
    padding-top:0 !important;
    padding-bottom: 0 !important;
    border:0 !important;
  }
}

</style>
